import React from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { normalizeQuantity } from '../utils/quantity';

const resolveQuantity = (item, variety, getItemQuantity) => {
  if (typeof getItemQuantity === 'function') {
    return getItemQuantity(item, variety);
  }
  return item?.quantity ?? 0;
};

const resolveDiscountAmount = ({ itemsSubtotal, orderDiscountPercentage, orderDiscountAmount }) => {
  const subtotal = Number(itemsSubtotal || 0);
  if (typeof orderDiscountPercentage === 'number') {
    return Math.floor(subtotal * (orderDiscountPercentage / 100));
  }
  return Number(orderDiscountAmount || 0);
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0';
  const hasDecimals = Math.abs(amount - Math.round(amount)) > 0.0001;
  return amount.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const normalizeLabel = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const buildItemDisplayName = (item, variety) => {
  const productName = normalizeLabel(item?.productName || variety?.productName || variety?.Product?.productName);
  const varietyName = normalizeLabel(item?.varietyName || variety?.varietyName);
  if (productName && varietyName) {
    if (productName.toLowerCase() === varietyName.toLowerCase()) {
      return productName;
    }
    return `${productName} - ${varietyName}`;
  }
  return productName || varietyName || 'Producto';
};

const CartPanel = ({
  items = [],
  getVariety,
  getItemQuantity,
  onItemClick,
  onRemoveItem,
  onQuantityChange,
  onQuantityIncrement,
  canEdit = true,
  getItemDisabled,
  headerContent,
  panelClassName = 'bg-white shadow-lg rounded-lg p-4',
  footerClassName = 'flex flex-col gap-1 mt-4 bottom-0',
  itemsSubtotal = 0,
  orderDiscountPercentage,
  orderDiscountAmount,
  totalAmount = 0,
  footerActions,
  pendingItemDiscounts = {},
  pendingOrderDiscount = null,
  onResumePendingItemDiscount,
  onResumePendingOrderDiscount,
  canOverridePriceType = false,
  getItemPriceTypeOptions,
  onItemPriceTypeChange,
  getItemPricing,
  showSummaryBreakdown = true,
}) => {
  const effectiveOrderDiscount = resolveDiscountAmount({
    itemsSubtotal,
    orderDiscountPercentage,
    orderDiscountAmount,
  });

  const handleItemClick = (item, variety) => {
    if (!onItemClick) return;
    onItemClick(item, variety);
  };

  return (
    <>
      <div className={panelClassName}>
        {headerContent && <div className="sticky top-0 bg-white pb-3 z-10">{headerContent}</div>}
        {items.length > 0 &&
          items.map((item, index) => {
            const variety = getVariety ? getVariety(item) : null;
            const imageSrc =
              variety?.VarietyPrices?.[item.priceIndex]?.priceImage ||
              variety?.varietyImage ||
              'https://placehold.co/50';
            const itemDisabled =
              !canEdit || (getItemDisabled ? getItemDisabled(item, variety) : false);
            const itemQuantity = normalizeQuantity(
              resolveQuantity(item, variety, getItemQuantity)
            );
            const disableDecrement = itemDisabled || itemQuantity <= 0;
            const itemPrice = item.price ?? 0;
            const itemDiscount = item.discount ?? 0;
            const discountedPrice = itemPrice - itemDiscount;
            const pricing = typeof getItemPricing === 'function'
              ? getItemPricing(item, variety, itemQuantity)
              : null;
            const displayItemPrice = Number.isFinite(Number(pricing?.unitPrice))
              ? Number(pricing.unitPrice)
              : itemPrice;
            const displayDiscountedPrice = Number.isFinite(Number(pricing?.discountedUnitPrice))
              ? Number(pricing.discountedUnitPrice)
              : discountedPrice;
            const displaySubtotal = Number.isFinite(Number(pricing?.subtotal))
              ? Number(pricing.subtotal)
              : (displayDiscountedPrice * itemQuantity);
            const hasDiscount = Math.abs(displayItemPrice - displayDiscountedPrice) > 0.0001;
            const taxFreeApplied = Boolean(pricing?.taxFreeApplied);
            const itemDescription =
              variety?.VarietyPrices?.[item.priceIndex]?.description
              || item?.metricUnit
              || 'unidad';
            const itemDisplayName = buildItemDisplayName(item, variety);
            const pendingKey = `${item.varietyId}-${item.priceIndex}`;
            const pendingEntry = pendingItemDiscounts?.[pendingKey] || null;
            const priceTypeOptions = typeof getItemPriceTypeOptions === 'function'
              ? getItemPriceTypeOptions(item, variety)
              : [];
            const canEditPriceType = canOverridePriceType
              && typeof onItemPriceTypeChange === 'function'
              && Array.isArray(priceTypeOptions)
              && priceTypeOptions.length > 0;
            const priceTypeValue = item?.priceTypeOverride
              ? (item?.priceType || '')
              : '';

            return (
              <div
                key={`${item.varietyId}-${item.priceIndex}-${index}`}
                className="relative bg-white border rounded-lg shadow p-4 cursor-pointer hover:bg-gray-50 mb-2"
                onClick={() => handleItemClick(item, variety)}
              >
                {canEdit && onRemoveItem && (
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveItem(item);
                    }}
                    className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded-full p-1"
                  >
                    <FiX size={14} />
                  </button>
                )}
                <div className="flex items-start justify-between gap-3 pr-8">
                  <img
                    src={imageSrc}
                    alt={itemDisplayName}
                    className="w-16 h-16 rounded object-cover flex-shrink-0"
                  />
                  <div className="w-[170px] min-w-0">
                    <div className="font-semibold text-sm break-words leading-snug">
                      {itemDisplayName}
                    </div>
                    <div className="text-xs text-gray-600 break-words leading-snug mt-0.5">
                      {itemDescription}
                    </div>
                    <div className="mt-2 flex items-center">
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!variety || !onQuantityIncrement) return;
                          onQuantityIncrement(variety.id, item.priceIndex, -1);
                        }}
                        className="w-9 h-9 min-w-[36px] min-h-[36px] flex-none shrink-0 bg-red-500 text-white rounded-l hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                        disabled={disableDecrement}
                      >
                        -
                      </button>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={itemQuantity}
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.target.select()}
                        onChange={(event) => {
                          event.stopPropagation();
                          if (!variety || !onQuantityChange) return;
                          onQuantityChange(variety.id, item.priceIndex, event.target.value);
                        }}
                        className="text-center text-sm border-t border-b border-gray-300 min-w-0 w-full h-9"
                        disabled={itemDisabled}
                      />
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!variety || !onQuantityIncrement) return;
                          onQuantityIncrement(variety.id, item.priceIndex, 1);
                        }}
                        className="w-9 h-9 min-w-[36px] min-h-[36px] flex-none shrink-0 bg-green-500 text-white rounded-r hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                        disabled={itemDisabled}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 inline-flex items-center">
                      Precio:{' '}
                      {hasDiscount ? (
                        <>
                          <span className="line-through mr-1">
                            ${formatMoney(displayItemPrice)}
                          </span>
                          <span>${formatMoney(displayDiscountedPrice)}</span>
                          <span className="ml-1 text-xs text-red-600">
                            ({displayItemPrice > 0 ? `-${Math.round(((displayItemPrice - displayDiscountedPrice) / displayItemPrice) * 100)}%` : '-0%'})
                          </span>
                        </>
                      ) : (
                        `$${formatMoney(displayItemPrice)}`
                      )}
                      {taxFreeApplied && (
                        <span
                          className="ml-1 inline-flex items-center text-blue-600"
                          title="Precio ajustado sin impuestos excluidos."
                          aria-label="Precio ajustado sin impuestos excluidos."
                        >
                          <FiInfo size={13} />
                        </span>
                      )}
                    </span>
                    <span className="font-semibold">
                      Subtotal: ${formatMoney(displaySubtotal)}
                    </span>
                  </div>
                </div>
                {canEditPriceType && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-gray-600">Tipo precio</label>
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                      value={priceTypeValue}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        onItemPriceTypeChange(item, event.target.value || '');
                      }}
                    >
                      <option value="">Auto</option>
                      {priceTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {item?.priceTypeOverride && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        Manual
                      </span>
                    )}
                  </div>
                )}
                {pendingEntry && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                    <span>Descuento pendiente</span>
                    {onResumePendingItemDiscount && (
                      <button
                        type="button"
                        className="text-amber-800 underline"
                        onClick={(event) => {
                          event.stopPropagation();
                          onResumePendingItemDiscount(pendingEntry);
                        }}
                      >
                        Continuar
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
      <div className={footerClassName}>
        {showSummaryBreakdown && (
          <>
            <div className="flex justify-between items-center text-sm">
              <span className="font-medium">Subtotal productos:</span>
              <span>${Number(itemsSubtotal || 0).toLocaleString('de-DE')}</span>
            </div>
            {effectiveOrderDiscount > 0 && (
              <div className="flex justify-between items-center text-sm text-red-600">
                <span className="font-medium">
                  Descuento orden
                  {orderDiscountPercentage != null ? ` (${orderDiscountPercentage}%)` : ''}:
                </span>
                <span>- ${effectiveOrderDiscount.toLocaleString('de-DE')}</span>
              </div>
            )}
            {pendingOrderDiscount && (
              <div className="flex items-center justify-between gap-2 text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded px-2 py-1">
                <span>Descuento de orden pendiente</span>
                {onResumePendingOrderDiscount && (
                  <button
                    type="button"
                    className="text-amber-800 underline"
                    onClick={() => onResumePendingOrderDiscount(pendingOrderDiscount)}
                  >
                    Continuar
                  </button>
                )}
              </div>
            )}
          </>
        )}
        <div className="flex justify-between items-center mt-1">
          <span className="text-sm font-bold">Total:</span>
          <span className="text-md font-bold text-blue-500">
            ${Number(totalAmount || 0).toLocaleString('de-DE')}
          </span>
        </div>
        {footerActions}
      </div>
    </>
  );
};

export default CartPanel;
